// UpdateContactLink.js - Complete Fixed Version
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import { DeleteIcon } from 'icons/materialIcons';
import getIconForType from 'utils/icons/getIconForType';
import { useCardContext } from 'contexts/CardContext';
import { useContactLinkContext } from 'contexts/ContactLinkContext';
import { useParams } from 'react-router-dom';

import ProductsServicesForm from './productsServices/ProductsServicesForm';
import PhoneInputField from 'components/common/PhoneInputField';
import loadGoogleMapsScript from 'utils/loadGoogleMapsScript';
import { cardDesign5Theme } from 'themes';
import { isPhoneContactType } from 'utils/phoneUtils';

const UpdateContactLink = ({
  link,
  onCancel,
  onUpdate,
  onDelete
}) => {
  const { cardState } = useCardContext();
  const { forceRefetchCard, currentCardId } = useContactLinkContext(); // ✅ ADD FORCE REFETCH
  const { cardId } = useParams();
  const cardLinkColor = cardState[cardId]?.cardLinkColor;
  const styles = cardDesign5Theme.card.cardDesign5;
  const mapRef = useRef(null);
  const autocompleteService = useRef(null);

  const [linkTitle, setLinkTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [icon, setIcon] = useState('');
  const [productsServicesData, setProductsServicesData] = useState({});
  const [phoneError, setPhoneError] = useState('');

  // Add states for address functionality
  const [workAddress, setWorkAddress] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [unit, setUnit] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [coordinates, setCoordinates] = useState(null); // Add coordinates state
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);

  // Handle phone input changes
  const handlePhoneChange = (value) => {
    setLinkUrl(value);
    setPhoneError('');
  };

  // Handle regular input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'linkTitle') {
      setLinkTitle(value);
    } else if (name === 'linkUrl') {
      setLinkUrl(value);
    }
  };

  // Geocoding function
  const geocodeAddress = async (address) => {
    return new Promise((resolve, reject) => {
      if (!window.google) {
        reject(new Error('Google Maps not loaded'));
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          const coords = {
            lat: location.lat(),
            lng: location.lng()
          };
          resolve(coords);
        } else {
          reject(new Error('Geocoding failed'));
        }
      });
    });
  };

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (link.type === 'Address') {
      if (window.google) {
        initializeAutocomplete();
        initializeMap();
      } else {
        loadGoogleMapsScript()
          .then(() => {
            initializeAutocomplete();
            initializeMap();
          })
          .catch(error => {
            console.error('Failed to load Google Maps:', error);
          });
      }
    }
  }, [link.type]);

  const initializeMap = () => {
    if (!mapRef.current || !window.google) return;

    const initialLocation = coordinates || link.details?.coordinates || { lat: 1.3521, lng: 103.8198 }; // Default to Singapore
    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: initialLocation,
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    });

    const markerInstance = new window.google.maps.Marker({
      position: initialLocation,
      map: mapInstance,
      draggable: true
    });

    markerInstance.addListener('dragend', () => {
      const position = markerInstance.getPosition();
      const coords = {
        lat: position.lat(),
        lng: position.lng()
      };
      setCoordinates(coords);

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: position }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setWorkAddress(results[0].formatted_address);
          setLinkUrl(results[0].formatted_address);
        }
      });
    });

    setMap(mapInstance);
    setMarker(markerInstance);
  };

  const initializeAutocomplete = () => {
    if (!window.google) return;
    autocompleteService.current = new window.google.maps.places.AutocompleteService();
  };

  const handleAddressChange = (e) => {
    const newValue = e.target.value;
    setWorkAddress(newValue);
    setLinkUrl(newValue);

    if (newValue && autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        { input: newValue },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setPredictions(predictions);
          }
        }
      );
    } else {
      setPredictions([]);
    }
  };

  // Add handler for manual address input with geocoding
  const handleAddressBlur = async () => {
    if (workAddress && !selectedPlace) {
      try {
        const coords = await geocodeAddress(workAddress);
        setCoordinates(coords);

        // Update map if it exists
        if (map && marker) {
          const position = new window.google.maps.LatLng(coords.lat, coords.lng);
          map.setCenter(position);
          marker.setPosition(position);
        }

        console.log('Geocoded coordinates for manual address:', coords);
      } catch (error) {
        console.error('Failed to geocode address:', error);
        // Still keep the address even if geocoding fails
      }
    }
  };

  const handlePlaceSelect = (prediction) => {
    const placesService = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );

    placesService.getDetails(
      { placeId: prediction.place_id },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setWorkAddress(place.formatted_address);
          setSelectedPlace(place);
          setPredictions([]);

          // Set coordinates from selected place
          if (place.geometry && place.geometry.location) {
            const coords = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            };
            setCoordinates(coords);

            // Update map and marker
            if (map && marker) {
              const newPosition = place.geometry.location;
              map.setCenter(newPosition);
              marker.setPosition(newPosition);
            }
          }

          // Extract zip code from address components
          const zipComponent = place.address_components.find(
            component => component.types.includes('postal_code')
          );
          if (zipComponent) {
            setZipCode(zipComponent.long_name);
          }

          // Set the full address as linkUrl
          const fullAddress = `${place.formatted_address}${unit ? ` Unit ${unit}` : ''}`;
          setLinkUrl(fullAddress);
        }
      }
    );
  };

  // Initialize form data when component mounts or link changes
  useEffect(() => {
    if (link) {
      console.log('UpdateContactLink: Initializing with link:', link);
      console.log('Link details structure:', JSON.stringify(link.details, null, 2));

      if (link.type === 'Products & Services') {
        // Extract data from all possible locations
        const details = link.details || {};

        // Build comprehensive data object with enhanced video handling
        const psData = {
          productTitle: details.additionalDetails?.productTitle || details.productTitle || details.linkTitle || link.type || '',
          description: details.additionalDetails?.description || details.description || details.desc || '',
          link: details.additionalDetails?.link || details.link || details.ctaButton?.link || details.linkUrl || details.url || '',
          ctaText: details.additionalDetails?.ctaText || details.ctaButton?.text || details.ctaText || details.buttonText || 'Learn More',
          // Enhanced array handling with fallbacks
          images: Array.isArray(details.additionalDetails?.images) ? [...details.additionalDetails.images] :
            Array.isArray(details.images) ? [...details.images] :
              details.images ? [details.images] : [],
          videos: Array.isArray(details.additionalDetails?.videos) ? [...details.additionalDetails.videos] :
            Array.isArray(details.videos) ? [...details.videos] :
              details.videos ? [details.videos] : [],
          ctaButton: {
            text: details.additionalDetails?.ctaText || details.ctaButton?.text || details.ctaText || 'Learn More',
            link: details.additionalDetails?.link || details.link || details.ctaButton?.link || details.linkUrl || ''
          }
        };

        // Handle legacy data structure where linkUrl might be 'custom'
        if (details.linkUrl === 'custom' && !psData.link) {
          psData.link = '';
        }

        // Ensure we have a valid product title
        if (!psData.productTitle && details.linkTitle) {
          psData.productTitle = details.linkTitle;
        }

        console.log('Extracted Products & Services data:', psData);
        console.log('Videos found:', psData.videos);
        console.log('Videos length:', psData.videos.length);
        console.log('Images found:', psData.images);

        setProductsServicesData(psData);
        setLinkTitle(psData.productTitle);
      } else {
        setLinkTitle(link.details?.linkTitle || '');
        setLinkUrl(link.details?.linkUrl || '');
        if (link.type === 'Address') {
          setWorkAddress(link.details?.linkUrl || link.details?.workAddress || '');
          setCoordinates(link.details?.coordinates || null); // Set existing coordinates
          setZipCode(link.details?.zipCode || '');
          setUnit(link.details?.unit || '');
        }
      }

      setIcon(link.details?.icon || '');
    }
  }, [link]);

  const handleTestLink = () => {
    if (link.type === 'Products & Services') {
      const testUrl = productsServicesData.link || productsServicesData.ctaButton?.link;
      if (testUrl) {
        const finalUrl = testUrl.startsWith('http') ? testUrl : `https://${testUrl}`;
        window.open(finalUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert('No link URL provided for this product/service.');
      }
      return;
    }

    if (link.type === 'Address' && workAddress) {
      const formattedLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workAddress)}`;
      window.open(formattedLink, '_blank', 'noopener,noreferrer');
    } else if (link.details?.formattedLink) {
      window.open(link.details.formattedLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleProductsServicesDataChange = (data) => {
    console.log('Products & Services data changed in UpdateContactLink:', data);
    console.log('Videos in updated data:', data.videos);
    setProductsServicesData(data);
    if (data.productTitle) {
      setLinkTitle(data.productTitle);
    }
  };

  // ✅ MODIFIED: Add force refetch after successful update
  const handleUpdate = async () => {
    let updateData;

    if (link.type === 'Products & Services') {
      // Enhanced Products & Services update with comprehensive video handling
      updateData = {
        id: link._id,
        type: link.type,
        details: {
          // Core product information
          productTitle: productsServicesData.productTitle || linkTitle,
          description: productsServicesData.description || '',
          link: productsServicesData.link || '',

          // CTA button information
          ctaText: productsServicesData.ctaText,
          ctaButton: {
            text: productsServicesData.ctaText,
            link: productsServicesData.link || ''
          },

          // Media arrays - ensure they're always arrays
          images: Array.isArray(productsServicesData.images) ?
            productsServicesData.images :
            (productsServicesData.images ? [productsServicesData.images] : []),
          videos: Array.isArray(productsServicesData.videos) ?
            productsServicesData.videos :
            (productsServicesData.videos ? [productsServicesData.videos] : []),

          // Standard fields for compatibility
          linkTitle: productsServicesData.productTitle || linkTitle,
          linkUrl: 'custom',
          formattedLink: 'custom',
          icon,

          // Additional Products & Services specific fields
          additionalDetails: {
            productTitle: productsServicesData.productTitle || linkTitle,
            description: productsServicesData.description || '',
            link: productsServicesData.link || '',
            ctaText: productsServicesData.ctaText,
            images: Array.isArray(productsServicesData.images) ?
              productsServicesData.images :
              (productsServicesData.images ? [productsServicesData.images] : []),
            videos: Array.isArray(productsServicesData.videos) ?
              productsServicesData.videos :
              (productsServicesData.videos ? [productsServicesData.videos] : [])
          }
        },
        isHighlighted: link.isHighlighted,
        isVisible: link.isVisible
      };

      console.log('Updating Products & Services with enhanced data:', updateData);
      console.log('Videos being sent:', updateData.details.videos);
      console.log('Images being sent:', updateData.details.images);
    } else if (link.type === 'Address') {
      // Address update with coordinates
      let formattedLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workAddress)}`;

      updateData = {
        id: link._id,
        type: link.type,
        details: {
          ...link.details,
          linkTitle,
          linkUrl: workAddress,
          workAddress,
          zipCode,
          unit,
          formattedLink,
          coordinates, // Include coordinates (works for both selected and geocoded addresses)
          icon
        },
        isHighlighted: link.isHighlighted,
        isVisible: link.isVisible
      };
    } else {
      let formattedLink = '';
      if (link.type === 'WhatsApp') {
        formattedLink = `https://wa.me/${linkUrl}`;
      } else {
        formattedLink = link.details?.formattedLink || '';
      }

      updateData = {
        id: link._id,
        type: link.type,
        details: {
          ...link.details,
          linkTitle,
          linkUrl,
          formattedLink,
          icon
        },
        isHighlighted: link.isHighlighted,
        isVisible: link.isVisible
      };
    }

    console.log('Final update data being sent:', updateData);
    console.log('🔍 UpdateContactLink: Calling onUpdate with:', {
      linkId: link._id,
      updateData,
      linkType: link.type,
      hasProductTitle: !!updateData.details.productTitle,
      hasDescription: !!updateData.details.description,
      hasImages: updateData.details.images?.length > 0,
      hasVideos: updateData.details.videos?.length > 0
    });

    try {
      // Call the original onUpdate function
      await onUpdate(link._id, updateData);

      // ✅ CRITICAL FIX: Force refetch the card after successful update
      const targetCardId = currentCardId || cardId;
      if (targetCardId) {
        console.log('🔍 UpdateContactLink: Force refetching card after successful update:', targetCardId);

        // Use setTimeout to ensure the database update has completed
        setTimeout(() => {
          forceRefetchCard(targetCardId);
        }, 1000); // 1 second delay to ensure database sync
      } else {
        console.warn('🔍 UpdateContactLink: No cardId available for force refetch');
      }

    } catch (error) {
      console.error('❌ UpdateContactLink: Error during update:', error);
      // Don't force refetch if update failed
      throw error;
    }
  };

  return (
    <Box sx={{ padding: 2, width: '100%', maxWidth: '900px', margin: '0 auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        {React.cloneElement(getIconForType(link.type), {
          sx: {
            mr: 2,
            fontSize: '24px',
            color: '#666',
            padding: '4px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
          }
        })}
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f' }}>
          Edit {link.type}
        </Typography>
      </Box>

      {link.type === 'Products & Services' ? (
        <Box sx={{ mb: 3 }}>
          <ProductsServicesForm
            onDataChange={handleProductsServicesDataChange}
            initialData={productsServicesData}
            cardId={cardId}
            userId={link.userId}
          />
        </Box>
      ) : link.type === 'Address' ? (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Address
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              id="work-address-input"
              value={workAddress}
              onChange={handleAddressChange}
              onBlur={handleAddressBlur} // Add onBlur handler for geocoding
              placeholder="Enter address"
              sx={{ mb: 2 }}
            />
            {predictions.length > 0 && (
              <Box sx={{
                position: 'absolute',
                zIndex: 1000,
                width: '100%',
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderRadius: '4px'
              }}>
                {predictions.map((prediction) => (
                  <Box
                    key={prediction.place_id}
                    onClick={() => handlePlaceSelect(prediction)}
                    sx={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                  >
                    {prediction.description}
                  </Box>
                ))}
              </Box>
            )}

            {/* ZIP and UNIT fields */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ZIP
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  UNIT
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                />
              </Box>
            </Box>

            {/* Map View - Updated to show for any address */}
            {workAddress && (
              <Box
                sx={{
                  mb: 2,
                  height: 200,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  position: 'relative',
                  cursor: 'pointer'
                }}
                onClick={() => handleTestLink()}
              >
                {coordinates ? (
                  // Show map with precise coordinates when available
                  <iframe
                    title="location-map"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    src={`https://www.google.com/maps/embed/v1/view?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&center=${coordinates.lat},${coordinates.lng}&zoom=15`}
                    allowFullScreen
                  />
                ) : (
                  // Fallback to search query when coordinates are not available
                  <iframe
                    title="location-map"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(workAddress)}`}
                    allowFullScreen
                  />
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Link Title
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              name="linkTitle"
              value={linkTitle}
              onChange={handleInputChange}
              placeholder="e.g., Office, Home, Shop"
              sx={{ mb: 2 }}
            />
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={handleTestLink}
            sx={{
              mb: 2,
              backgroundColor: cardLinkColor || '#000',
              '&:hover': { backgroundColor: cardLinkColor ? `${cardLinkColor}dd` : '#333' },
              textTransform: 'none',
              borderRadius: '8px',
            }}
          >
            Test Your Link
          </Button>
        </>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {link.type === 'Address' ? 'Address' : 'Link URL'}
            </Typography>
            {isPhoneContactType(link.type) ? (
              <PhoneInputField
                value={linkUrl}
                onChange={handlePhoneChange}
                error={phoneError}
                country="sg"
                placeholder="Enter phone number"
                containerStyle={{ marginBottom: '16px' }}
              />
            ) : (
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                name="linkUrl"
                value={linkUrl}
                onChange={handleInputChange}
                placeholder={link.type === 'Address' ? 'Enter address' : 'Enter URL'}
                sx={{ mb: 2 }}
              />
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Link Title
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              name="linkTitle"
              value={linkTitle}
              onChange={handleInputChange}
              placeholder={link.type}
              sx={{ mb: 2 }}
            />
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={handleTestLink}
            sx={{
              mb: 2,
              backgroundColor: cardLinkColor || '#000',
              '&:hover': { backgroundColor: cardLinkColor ? `${cardLinkColor}dd` : '#333' },
              textTransform: 'none',
              borderRadius: '8px',
            }}
          >
            Test Your Link
          </Button>
        </>
      )}

      {/* ✅ DEBUG: Add debug info for Products & Services videos */}
      {link.type === 'Products & Services' && process.env.NODE_ENV === 'development' && (
        <Box sx={{
          mt: 2,
          p: 2,
          backgroundColor: '#f0f0f0',
          borderRadius: 1,
          fontSize: '12px'
        }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            Debug Info - Products & Services Videos:
          </Typography>
          <div>Current Videos Count: {productsServicesData.videos?.length || 0}</div>
          <div>Videos Array: {JSON.stringify(productsServicesData.videos, null, 2)}</div>
          <div>Current Card ID: {currentCardId || cardId}</div>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(link._id)}
          sx={{
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#ffebee'
            }
          }}
        >
          Remove
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              color: '#666',
              borderColor: '#ddd',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            sx={{
              backgroundColor: cardLinkColor || '#000',
              '&:hover': { backgroundColor: cardLinkColor ? `${cardLinkColor}dd` : '#333' },
              textTransform: 'none',
              borderRadius: '8px',
            }}
          >
            Update
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default UpdateContactLink;